<template>
  <div class="card__container" :class="{ 'card-active': isCardActive }">
    <!-- Info Start -->
    <div class="p-2">
      <header
        class="d-flex flex-column justify-content-sm-between align-items-sm-center flex-sm-row"
      >
        <span class="text__title">Visitor Information</span>
        <div class="d-flex justify-content-between justify-content-sm-start">
          <atom-button
            v-if="isShowResendEmailButton"
            buttonId="btn-resend-email"
            class="mr-1"
            iconLeft="MailIcon"
            buttonStyle="border"
            buttonColor="secondary"
            @click="onClickResendEmail"
            >Resend Email</atom-button
          >
          <atom-button
            v-if="
              isShowEditVisitorButton &&
              (hasUpdatePermission || !isInternalAccess)
            "
            buttonId="btn-edit-info"
            iconLeft="EditIcon"
            buttonStyle="flat"
            buttonColor="secondary"
            :isDisable="isShowAccessForm"
            @click="onClickEditInfo"
          ></atom-button>
          <atom-button
            v-if="
              isShowDeleteVisitorButton &&
              (hasUpdatePermission || !isInternalAccess)
            "
            buttonId="btn-delete-info"
            iconLeft="Trash2Icon"
            buttonStyle="flat"
            buttonColor="secondary"
            @click="onClickDeleteInfo"
          ></atom-button>
        </div>
      </header>

      <div class="mt-1 d-flex flex-wrap">
        <div class="d-flex flex-column flex-md-row col-12 col-lg-4 p-0">
          <div class="text__topic">Name</div>
          <div class="text__data ml-md-1">
            {{ `${visitor.firstname} ${visitor.lastname}` }}
          </div>
        </div>
        <div
          class="d-flex flex-column flex-md-row col-12 col-lg-4 p-0 mt-1 mt-lg-0"
        >
          <div class="text__topic">ID Type</div>
          <div class="text__data ml-md-1">
            {{ visitor.idType.name }}
          </div>
        </div>
        <div
          class="d-flex flex-column flex-md-row col-12 col-lg-4 p-0 mt-1 mt-lg-0"
        >
          <div class="text__topic">ID/Passport Number</div>
          <div class="text__data ml-md-1">{{ visitor.idNumber }}</div>
        </div>
        <div class="d-flex flex-column flex-md-row col-12 col-lg-4 p-0 mt-1">
          <div class="text__topic">Email</div>
          <div class="text__data ml-md-1">{{ visitor.email }}</div>
        </div>
        <div class="d-flex flex-column flex-md-row col-12 col-lg-4 p-0 mt-1">
          <div class="text__topic">Phone</div>
          <div class="text__data ml-md-1">{{ visitor.phone }}</div>
        </div>
      </div>
    </div>
    <!-- Info End -->

    <!-- Self Check-in Start -->
    <div v-if="isShowSelfCheckIn">
      <hr />
      <div class="p-2">
        <label class="text__self-check-in">Self check-in</label>
        <div class="mt-1 d-md-flex">
          <div class="image-container">
            <img
              v-if="selfCheckIn.image && !isImageError"
              :src="selfCheckIn.image"
              class="avatar__size"
              rounded
              @error="isImageError = true"
            />
            <b-avatar
              v-else
              variant="light-secondary"
              rounded="sm"
              class="avatar__size"
            ></b-avatar>
          </div>
          <div class="mt-2 mt-md-0 ml-md-2">
            <div class="d-md-flex mt-1 mt-md-0">
              <label class="text-topic-self-check-in w-145px">
                Self Check In Date:
              </label>
              <label
                class="ml-md-1 text-data-highlight-self-check-in w-400px"
                >{{
                  selfCheckIn.selfCheckInAt
                    ? formatDateTimeWithDay(selfCheckIn.selfCheckInAt)
                    : "-"
                }}</label
              >
            </div>
            <div class="d-md-flex mt-1 mt-md-0">
              <label class="text-topic-self-check-in w-145px">Name:</label>
              <label class="ml-md-1 text-data-self-check-in w-400px">{{
                selfCheckIn.name
              }}</label>
            </div>
            <div class="d-md-flex mt-1 mt-md-0">
              <label class="text-topic-self-check-in w-145px">
                ID/Passport Number:
              </label>
              <label class="ml-md-1 text-data-self-check-in w-400px">{{
                selfCheckIn.idNumber
              }}</label>
            </div>
            <div class="d-md-flex mt-1 mt-md-0">
              <label class="text-topic-self-check-in w-145px"
                >ID Expired date:</label
              >
              <label class="ml-md-1 text-data-self-check-in w-400px">{{
                selfCheckIn.idExpiredDate
                  ? setDateTimeFormat(selfCheckIn.idExpiredDate, "d LLLL yyyy")
                  : "-"
              }}</label>
            </div>
            <div class="d-md-flex mt-1 mt-md-0">
              <label class="text-topic-self-check-in w-145px"
                >Nationality:</label
              >
              <label class="ml-md-1 text-data-self-check-in w-400px">{{
                selfCheckIn.nationality
              }}</label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Self Check-in End -->

    <!-- Access Info Start -->
    <div v-if="accessBuilding.length || isCanUpdateAccess">
      <hr />
      <div class="p-2">
        <div class="d-flex align-items-center mb-12px h-38px">
          <label class="text__access-info-topic mb-0">Access information</label>
          <atom-button
            v-if="isCanUpdateAccess && hasUpdatePermission"
            buttonId="btn-add-access"
            iconLeft="PlusIcon"
            :isDisable="isShowAccessForm"
            @click="onClickAddAccessInfo"
            class="ml-1"
          ></atom-button>
        </div>

        <div v-for="(building, index) in accessBuilding" :key="index">
          <div
            v-if="
              isEnableFeatureShowSomeAccess
                ? isShowAccessAll || index < minimumShowAccessCard
                : true
            "
            class="access-building-container"
            :class="[index === 0 ? 'mt-24px' : 'mt-12px']"
          >
            <!-- Building Card Start -->
            <div class="d-flex justify-content-between align-items-start">
              <div
                v-if="building.accessBuildingId"
                class="w-100 d-flex flex-column mb-1 w-building-card"
              >
                <div
                  class="d-flex flex-column flex-md-row justify-content-between"
                >
                  <div class="d-md-flex col-12 col-md-6 py-0 px-0 pr-md-1">
                    <label class="text-access-building-topic mr-2 mb-0"
                      >First Check In Date</label
                    >
                    <label
                      class="mb-0"
                      :class="[
                        index === 0
                          ? 'text-access-building-highlight-data'
                          : 'text-access-self-check-in-data',
                      ]"
                      >{{
                        building.checkInDate
                          ? formatDateTimeWithDay(building.checkInDate)
                          : "-"
                      }}</label
                    >
                  </div>
                  <div class="d-md-flex col-12 col-md-6 mt-1 mt-md-0 p-0">
                    <label class="text-access-building-topic mr-2 mb-0"
                      >Last Check Out Date</label
                    >
                    <label class="text-access-self-check-in-data mb-0">{{
                      building.checkOutDate
                        ? formatDateTimeWithDay(building.checkOutDate)
                        : "-"
                    }}</label>
                  </div>
                </div>
              </div>

              <div
                v-if="
                  isInternalAccess &&
                  isCanUpdateAccess &&
                  (!isShowAccessForm || currentIndexOpenAccessForm !== index) &&
                  hasUpdatePermission
                "
                class="d-flex"
              >
                <atom-button
                  buttonId="btn-edit-access-info"
                  iconLeft="EditIcon"
                  buttonStyle="flat"
                  buttonColor="secondary"
                  :isDisable="isShowAccessForm"
                  class="btn__edit-access-info"
                  @click="onClickEditAccessInfo(index)"
                ></atom-button>
                <atom-button
                  v-if="accessBuilding.length > 1"
                  buttonId="btn-delete-access-info"
                  iconLeft="Trash2Icon"
                  buttonStyle="flat"
                  buttonColor="secondary"
                  :isDisable="isShowAccessForm"
                  class="btn__delete-access-info ml-8px"
                  @click="onClickDeleteAccessInfo(index)"
                ></atom-button>
              </div>
            </div>

            <div v-if="isInternalAccess">
              <atom-input
                v-if="isEditAccessForm && currentIndexOpenAccessForm === index"
                v-model="building.buildingCard"
                title="Building Card No"
                placeholder="Building Card No"
                :isAsterisk="true"
                class="col-12 col-lg-4 px-0"
              />
              <div v-else class="d-md-flex">
                <label class="text-access-building-topic mr-2 mb-0"
                  >Building Card No</label
                >
                <label class="text-access-self-check-in-data-bold mb-0">{{
                  building.buildingCard || "-"
                }}</label>
              </div>
            </div>
            <!-- Building Card Start -->

            <!-- NTDC Card Start -->
            <div
              v-if="building.accessCard.length || isInternalAccess"
              class="mt-30px"
            >
              <div class="d-flex justify-content-between align-items-center">
                <label class="text-access-topic">NTDC Card</label>
                <atom-button
                  v-if="
                    isInternalAccess && isCanUpdateAccess && hasUpdatePermission
                  "
                  buttonId="btn-add-ntdc-card"
                  buttonStyle="border"
                  :isDisable="isShowAccessForm"
                  @click="onClickAddNtdcCard(index)"
                >
                  <span class="d-lg-none">Add</span
                  ><span class="d-none d-lg-block"
                    >Add NTDC Card</span
                  ></atom-button
                >
              </div>

              <div
                v-if="isAddAccessForm && currentIndexOpenAccessForm === index"
                class="d-flex flex-column flex-lg-row mt-1"
              >
                <div class="col-12 col-lg-3 p-0 mt-1 mt-lg-0">
                  <atom-datepicker
                    datepickerId="check-in-date"
                    title="Check In Date"
                    v-model="accessForm.checkInDate"
                    placeholder="Check In Date"
                    :maxDate="accessForm.checkOutDate"
                    type="dateNtime"
                    :showSetTimeNowButton="true"
                  ></atom-datepicker>
                </div>
                <div class="col-12 col-lg-3 p-0 mt-1 mt-lg-0 ml-access-form">
                  <atom-datepicker
                    datepickerId="check-out-date"
                    title="Check Out Date"
                    v-model="accessForm.checkOutDate"
                    placeholder="Check Out Date"
                    :minDate="accessForm.checkInDate"
                    :showSetTimeNowButton="true"
                    type="dateNtime"
                  ></atom-datepicker>
                </div>
                <atom-input
                  v-model="accessForm.location"
                  title="Location"
                  placeholder="Location"
                  class="col-12 col-lg-2 p-0 mt-1 mt-lg-0 ml-access-form"
                />
                <atom-input
                  v-model="accessForm.cardType"
                  title="Card Type"
                  placeholder="Card Type"
                  class="col-12 col-lg-1 p-0 mt-1 mt-lg-0 ml-access-form"
                />
                <atom-input
                  v-model="accessForm.cardNumber"
                  title="Card No"
                  placeholder="Card No"
                  class="col-12 col-lg-2 p-0 mt-1 mt-lg-0 ml-access-form"
                />
              </div>

              <div
                v-if="
                  building.accessCard.length &&
                  (!isEditAccessForm || currentIndexOpenAccessForm !== index)
                "
                class="d-none d-md-flex mt-14px"
              >
                <label class="col-12 col-md-3 p-0 mr-md-1 text-access-title"
                  >Check in date</label
                >
                <label class="col-12 col-md-3 p-0 text-access-title"
                  >Check Out date</label
                >
                <label class="col-12 col-md-2 p-0 text-access-title"
                  >Location</label
                >
                <label class="col-12 col-md-2 p-0 text-access-title"
                  >Card Type</label
                >
                <label class="col-12 col-md-2 p-0 text-access-title"
                  >Card No</label
                >
              </div>

              <div
                v-for="(access, accessIndex) in building.accessCard"
                :key="accessIndex"
                :class="{ 'mt-10px': accessIndex === 0 }"
              >
                <div
                  v-if="
                    isEditAccessForm && currentIndexOpenAccessForm === index
                  "
                  class="d-flex flex-column flex-lg-row"
                  :class="{ 'mt-1': accessIndex > 0 }"
                >
                  <div class="col-12 col-lg-3 p-0 mt-1 mt-lg-0">
                    <atom-datepicker
                      datepickerId="check-in-date"
                      title="Check In Date"
                      v-model="access.checkInDate"
                      placeholder="Check In Date"
                      :maxDate="access.checkOutDate"
                      type="dateNtime"
                      :showSetTimeNowButton="true"
                    ></atom-datepicker>
                  </div>
                  <div class="col-12 col-lg-3 p-0 mt-1 mt-lg-0 ml-access-form">
                    <atom-datepicker
                      datepickerId="check-out-date"
                      title="Check Out Date"
                      v-model="access.checkOutDate"
                      placeholder="Check Out Date"
                      :minDate="access.checkInDate"
                      type="dateNtime"
                      :showSetTimeNowButton="true"
                    ></atom-datepicker>
                  </div>
                  <atom-input
                    v-model="access.location"
                    title="Location"
                    placeholder="Location"
                    class="col-12 col-lg-2 p-0 mt-1 mt-lg-0 ml-access-form"
                  />
                  <atom-input
                    v-model="access.cardType"
                    title="Card Type"
                    placeholder="Card Type"
                    class="col-12 col-lg-1 p-0 mt-1 mt-lg-0 ml-access-form"
                  />
                  <atom-input
                    v-model="access.cardNumber"
                    title="Card No"
                    placeholder="Card No"
                    class="col-12 col-lg-2 p-0 mt-1 mt-lg-0 ml-access-form"
                  />
                  <div
                    class="d-flex justify-content-end align-items-end mt-1 mt-lg-0"
                  >
                    <atom-button
                      v-if="isEditAccessForm && building.accessCard.length > 1"
                      buttonId="btn-delete-access-info"
                      :isOnlyIcon="true"
                      iconLeft="Trash2Icon"
                      buttonStyle="flat"
                      buttonColor="secondary"
                      @click="onClickDeleteAccessCard(index, accessIndex)"
                      class="w-38px"
                    />
                  </div>
                </div>

                <div v-else class="d-flex flex-column flex-md-row">
                  <label class="d-md-none col-12 p-0 text-access-title"
                    >Check in date</label
                  >
                  <label class="col-12 col-md-3 p-0 mr-md-1 text-access-data">{{
                    access.checkInDate
                      ? formatDateTimeWithDay(access.checkInDate)
                      : "-"
                  }}</label>

                  <label class="d-md-none mt-1 col-12 p-0 text-access-title"
                    >Check Out date</label
                  >
                  <label class="col-12 col-md-3 p-0 text-access-data">{{
                    access.checkOutDate
                      ? formatDateTimeWithDay(access.checkOutDate)
                      : "-"
                  }}</label>

                  <label class="d-md-none mt-1 col-12 p-0 text-access-title"
                    >Location</label
                  >
                  <label class="col-12 col-md-2 p-0 text-access-data">{{
                    access.location || "-"
                  }}</label>
                  <label class="d-md-none mt-1 col-12 p-0 text-access-title"
                    >Card Type</label
                  >
                  <label class="col-12 col-md-2 p-0 text-access-data">{{
                    access.cardType || "-"
                  }}</label>

                  <label class="d-md-none mt-1 col-12 p-0 text-access-title"
                    >Card No</label
                  >
                  <label class="col-12 col-md-2 p-0 text-access-data">{{
                    access.cardNumber || "-"
                  }}</label>

                  <hr
                    class="d-md-none"
                    :class="{
                      'd-none': accessIndex === building.accessCard.length - 1,
                    }"
                  />
                </div>
              </div>

              <div
                v-if="isShowAccessForm && currentIndexOpenAccessForm === index"
                class="d-flex justify-content-end mt-2"
              >
                <atom-button
                  :isDisable="!building.buildingCard"
                  buttonId="btn-save-access-form"
                  @click="onClickSaveAccessForm(index)"
                >
                  <span class="d-lg-none">Save</span
                  ><span class="d-none d-lg-block">Save Changes</span>
                </atom-button>
                <atom-button
                  buttonId="btn-cancel-save-access-form"
                  buttonStyle="border"
                  buttonColor="secondary"
                  @click="onClickCancelSaveAccessForm(index)"
                  class="ml-12px"
                  >Cancel</atom-button
                >
              </div>
            </div>
            <!-- NTDC Card End -->
          </div>
        </div>

        <div
          v-if="isEnableFeatureShowSomeAccess && accessBuilding.length > 2"
          class="d-flex justify-content-center align-items-center mt-8px"
          @click="isShowAccessAll = !isShowAccessAll"
        >
          <label class="text-access-more-or-less pb-0">{{
            !isShowAccessAll ? "See more" : "See less"
          }}</label>
          <feather-icon
            class="icon-access-more-or-less"
            :icon="`${!isShowAccessAll ? 'ChevronDownIcon' : 'ChevronUpIcon'}`"
            size="14"
          />
        </div>
      </div>
    </div>
    <!-- Access Info End -->

    <!-- Modal Start -->
    <molecule-modal-dialog
      :modalId="`modal-confirm-discard-change-access-info-${visitor.id}`"
      :isTextCenter="true"
      textTitle="Do you want to leave without finishing?"
      buttonTextLeft="Confirm"
      @clickButtonLeft="onClickDiscard"
    >
    </molecule-modal-dialog>
    <!-- Modal End -->

    <molecule-modal-dialog
      :modalId="`modal-resend-email-${visitor.id}`"
      :isTextCenter="true"
      icon="MailIcon"
      iconColor="secondary"
      textTitle="Do you want to resend Email?"
      :textSubtitle="`This action will resend the email \nto ${visitor.firstname} ${visitor.lastname} (${visitor.email})`"
      buttonTextLeft="Confirm"
      @clickIconClose="closeModal()"
      @clickButtonLeft="onClickConfirmResendEmail"
    />

    <molecule-modal-dialog
      :modalId="`modal-confirm-delete-access-${visitor.id}`"
      textTitle="Do you want to delete?"
      textSubtitle="You won’t be able to revert this!"
      buttonTextLeft="Delete"
      buttonColorLeft="danger"
      buttonTextRight="Cancel"
      :isTextCenter="true"
      @clickButtonLeft="onClickConfirmDeleteAccessInfo"
    >
    </molecule-modal-dialog>
  </div>
</template>

<script>
import { DateTime } from "luxon"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import mixinsDatetime from "@/mixins/mixinsDatetime"
import { BAvatar } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomDatepicker from "@/components/atoms/AtomDatepicker.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"

export default {
  name: "MoleculeVisitorCard",
  components: {
    BAvatar,
    AtomButton,
    AtomDatepicker,
    AtomInput,
    MoleculeModalDialog,
  },
  mixins: [mixinsObjectHelper, mixinsDatetime],
  props: {
    visitor: {
      type: Object,
      required: true,
    },
    status: {
      type: String,
      required: false,
      default: "NEW",
    },
    isEditVisitor: {
      type: Boolean,
      required: false,
      default: false,
    },
    idTypeOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    isCardActive: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDeletable: {
      type: Boolean,
      required: false,
      default: true,
    },
    hasUpdatePermission: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: [
    "onClickEditVisitorInfo",
    "onClickDeleteVisitorInfo",
    "updateAccessBuilding",
    "isShowAccessForm",
    "onResendEmail",
  ],
  data() {
    return {
      modalId: null,
      isEnableFeatureShowSomeAccess: false,
      isShowAccessAll: false,
      isAddAccessForm: false,
      isEditAccessForm: false,

      currentIndexOpenAccessForm: -1,

      minimumShowAccessCard: 2,

      visitorInfo: {
        firstname: null,
        lastname: null,
        idType: null,
        idNumber: null,
        email: null,
        phone: null,
      },

      selfCheckIn: {
        dateFirstCheckIn: null,
        dateLastCheckIn: null,
        selfCheckInAt: null,
        name: null,
        idNumber: null,
        idExpiredDate: null,
        nationality: null,
        address: null,
        image: null,
      },

      accessBuilding: [],
      accessBuidldingIndex: null,

      accessForm: {
        checkInDate: null,
        checkOutDate: null,
        location: "",
        cardType: "",
        cardNumber: "",
      },

      tempAccessCard: null,
      isImageError: false,
    }
  },
  computed: {
    isInternalAccess() {
      return this.$route.meta.type === "internal"
    },
    isCreating() {
      return this.status === "NEW"
    },
    isWaiting() {
      return this.status === "WAITING"
    },
    isApproved() {
      return this.status === "APPROVED"
    },
    isAccess() {
      return this.status === "ACCESS"
    },
    isRejected() {
      return this.status === "REJECTED"
    },
    isClosed() {
      return this.status === "CLOSED"
    },
    isShowEditVisitorButton() {
      return (
        this.isCreating ||
        this.isWaiting ||
        (this.isInternalAccess && !(this.isRejected || this.isClosed))
      )
    },
    isShowDeleteVisitorButton() {
      return (this.isCreating || this.isWaiting) && this.isDeletable
    },
    isShowSelfCheckIn() {
      return (
        this.isInternalAccess &&
        (this.isAccess || this.isClosed || this.isApproved) &&
        !!this.selfCheckIn.name
      )
    },
    isShowAccessForm() {
      return (
        (this.isAddAccessForm || this.isEditAccessForm) &&
        this.currentIndexOpenAccessForm > -1
      )
    },
    isCanUpdateAccess() {
      return this.isInternalAccess && (this.isAccess || this.isApproved)
    },
    isShowResendEmailButton() {
      return this.isInternalAccess && (this.isAccess || this.isApproved)
    },
  },
  watch: {
    visitor: {
      deep: true,
      handler() {
        this.setDataFromVisitorProp()
      },
    },
    isShowAccessForm() {
      this.$emit("isShowAccessForm", this.isShowAccessForm)
    },
  },
  mounted() {
    this.setDataFromVisitorProp()
  },
  methods: {
    setDataFromVisitorProp() {
      this.visitorInfo = this.deepCopyObject({
        id: this.visitor.id,
        firstname: this.visitor.firstname,
        lastname: this.visitor.lastname,
        idNumber: this.visitor.idNumber,
        email: this.visitor.email,
        phone: this.visitor.phone,
        idType: this.idTypeOptions.find(
          (type) => type.id === this.visitor.idType
        ),
      })

      if (this.visitor?.selfCheckIn) {
        this.selfCheckIn = this.deepCopyObject(this.visitor.selfCheckIn)
      }

      this.accessBuilding = this.deepCopyArray(
        this.visitor?.accessBuilding || []
      )
    },

    onClickEditInfo() {
      this.$emit("onClickEditVisitorInfo", this.visitor.id)
    },

    onClickDeleteInfo() {
      this.$emit("onClickDeleteVisitorInfo", this.visitor.id)
    },

    onClickResendEmail() {
      this.openModal(`modal-resend-email-${this.visitor.id}`)
    },

    onClickConfirmResendEmail() {
      this.$emit("onResendEmail", this.visitor.id)
      this.closeModal()
    },

    onClickAddAccessInfo() {
      if (this.isShowAccessForm) {
        this.openModal(
          `modal-confirm-discard-change-access-info-${this.visitor.id}`
        )
      } else {
        this.accessBuilding = [
          {
            checkInDate: null,
            checkOutDate: null,
            buildingCard: "",
            accessCard: [
              {
                checkInDate: null,
                checkOutDate: null,
                location: "",
                cardType: "",
                cardNumber: "",
              },
            ],
          },
          ...this.accessBuilding,
        ]

        this.currentIndexOpenAccessForm = 0
        this.isEditAccessForm = true
      }
    },

    onClickEditAccessInfo(index) {
      this.currentIndexOpenAccessForm = index
      this.isEditAccessForm = true
    },

    onClickDeleteAccessInfo(index) {
      this.openModal(`modal-confirm-delete-access-${this.visitor.id}`)
      this.accessBuidldingIndex = index
    },

    onClickConfirmDeleteAccessInfo() {
      this.accessBuilding.splice(this.accessBuidldingIndex, 1)
      this.updateAccessBuilding()
      this.closeModal()
    },

    onClickAddNtdcCard(index) {
      this.accessForm = {
        checkInDate: null,
        checkOutDate: null,
        location: "",
        cardType: "",
        cardNumber: "",
      }

      this.currentIndexOpenAccessForm = index
      this.isAddAccessForm = true
    },

    onClickSaveAccessForm(index) {
      if (this.isAddAccessForm && this.currentIndexOpenAccessForm === index) {
        this.accessBuilding[index].accessCard = [
          this.accessForm,
          ...this.accessBuilding[index].accessCard,
        ]
      }

      if (
        !this.accessBuilding[index].accessBuildingId &&
        !this.accessBuilding[index].buildingCard
      ) {
        this.accessBuilding.splice(index, 1)
      } else {
        this.accessBuilding[index].accessCard = this.deepCopyArray(
          this.filterAccessForm(index)
        )

        this.updateAccessBuilding()
      }

      this.forceCloseAccessForm()
    },

    onClickCancelSaveAccessForm(index = -1) {
      if (index !== -1 && !this.accessBuilding[index].accessBuildingId) {
        this.openModal(
          `modal-confirm-discard-change-access-info-${this.visitor.id}`
        )
      } else {
        this.accessBuilding = this.deepCopyArray(this.visitor?.accessBuilding)
        this.forceCloseAccessForm()
      }
    },

    onClickDeleteAccessCard(index, accessIndex) {
      this.accessBuilding[index].accessCard.splice(accessIndex, 1)
    },

    updateAccessBuilding() {
      const accessBuildingInfo = this.accessBuilding.map((building) => {
        return {
          ...building,
          checkInDate: building.checkInDate ? building.checkInDate : null,
          checkOutDate: building.checkOutDate ? building.checkOutDate : null,
          accessCard: building.accessCard.map((access) => {
            return {
              ...access,
              checkInDate: access.checkInDate ? access.checkInDate : null,
              checkOutDate: access.checkOutDate ? access.checkOutDate : null,
            }
          }),
        }
      })
      this.$emit(
        "updateAccessBuilding",
        this.visitorInfo.id,
        accessBuildingInfo
      )
      this.accessBuidldingIndex = null
    },

    forceCloseAccessForm() {
      this.isAddAccessForm = false
      this.isEditAccessForm = false
      this.currentIndexOpenAccessForm = -1
    },

    filterAccessForm(index) {
      return this.accessBuilding[index].accessCard.filter(
        (i) =>
          !(
            !i.checkInDate &&
            !i.checkOutDate &&
            !i.location &&
            !i.cardType &&
            !i.cardNumber
          )
      )
    },

    onClickDiscard() {
      this.closeModal()
      this.onClickCancelSaveAccessForm()
    },

    // Datetime Start
    formatDateTimeWithDay(datetime) {
      return DateTime.fromISO(datetime).toFormat("cccc, d LLLL yyyy HH:mm")
    },
    // Datetime End

    // Modal Start
    openModal(modalId) {
      this.modalId = modalId
      this.$bvModal.show(this.modalId)
    },

    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
    },
    // Modal End
  },
}
</script>

<style lang="scss" scoped>
hr {
  width: 100%;
  stroke-width: 1px;
  stroke: #e9ecef;
}

.card__container {
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.06);
  margin-top: 15px;
}

.avatar__size {
  border-radius: 6px;
  width: 80px;
  height: 100px;
  object-fit: cover;
}

.text__title {
  color: #5e5873;
  font-weight: 500;
  font-size: 14px;
  text-transform: uppercase;
}

.text__topic {
  color: #b9b9c3;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.text__data {
  display: inline-flex;
  padding: 1px 9px;
  align-items: center;
  gap: 3px;
  border-radius: 4px;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
  font-weight: 600;
  font-size: 12px;
}

.text__self-check-in,
.text__access-info-topic {
  color: #b9b9c3;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
}

.text-topic-self-check-in {
  color: #5e5873;
  font-size: 12px;
  font-weight: 400;
}

.text-data-highlight-self-check-in {
  color: #7367f0;
  font-size: 12px;
  font-weight: 600;
}

.text-data-self-check-in {
  color: #5e5873;
  font-size: 12px;
  font-weight: 600;
}

.access-building-container {
  border-radius: 6px;
  border: 1px solid #b9b9c3;
  background: #fff;
  padding: 19px 19px 30px;
}

.text-access-building-topic {
  color: #5e5873;
  font-size: 14px;
  font-weight: 400;
}

.text-access-building-highlight-data {
  color: #7367f0;
  font-size: 14px;
  font-weight: 600;
}

.text-access-self-check-in-data {
  color: #6e6b7b;
  font-size: 14px;
  font-weight: 600;
}

.text-access-self-check-in-data-bold {
  color: #6e6b7b;
  font-size: 14px;
  font-weight: 700;
}

.text-access-topic {
  color: #6e6b7b;
  font-size: 14px;
  font-weight: 600;
  text-decoration-line: underline;
}

.text-access-title {
  color: #b9b9c3;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.text-access-data {
  color: #5e5873;
  font-size: 14px;
  font-weight: 400;
}

.text-access-more-or-less {
  color: #b9b9c3;
  font-size: 12px;
  font-weight: 400;
  text-decoration-line: underline;
  cursor: pointer;
  margin-right: 4px;
}

.icon-access-more-or-less {
  color: #b9b9c3;
  cursor: pointer;
}

.h-38px {
  height: 38px;
}

.w-38px {
  width: 38px;
}

.w-145px {
  width: 145px;
}

.w-400px {
  width: 400px;
}

.mt-8px {
  margin-top: 8px;
}

.mt-10px {
  margin-top: 10px;
}

.mt-12px {
  margin-top: 12px;
}

.mt-14px {
  margin-top: 14px;
}

.mt-24px {
  margin-top: 24px;
}

.mt-30px {
  margin-top: 30px;
}

.ml-8px {
  margin-left: 8px;
}

.ml-12px {
  margin-left: 12px;
}

.w-building-card {
  width: calc(100% - 52px);
  margin-right: 8px;
}

.ml-access-form {
  margin-left: calc(((100% / 12) - 38px) / 4);
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  .avatar__size {
    width: 110px;
    height: 138px;
  }
}

.btn__edit-access-info ::v-deep .padding-icon,
.btn__delete-access-info ::v-deep .padding-icon {
  padding: 0;
}

.card-active {
  border: 1px solid rgba(121, 97, 249, 1);
  box-shadow: 0px 2px 5px rgba(114, 102, 239, 0.3);
  border-radius: 10px;
}
</style>
